import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { GridOverlay } from '@mui/x-data-grid-pro';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useParams } from 'react-router';
import { Checkbox } from '@mui/material';
import { DatabaseFeatureFlagKey } from 'libs/end-to-end/src/database-feature-flags';
import { DatabaseFeatureFlagKeys } from 'libs/end-to-end/src/database-feature-flags';
import { DatabaseFeatureFlagsResponseDto } from 'libs/end-to-end/src/database-feature-flags';
import { UpdateDatabaseFeatureFlagsDto } from 'libs/end-to-end/src/database-feature-flags';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { formatDateStandard } from 'libs/end-to-end/src/dates';

const ErrorOverlay = ({ message }: { message: string }) => {
  return (
    <GridOverlay>
      <S2Typography
        variant="body-6"
        textColorVariant="red-600"
        translatedContent={message}
      />
    </GridOverlay>
  );
};

type FeatureFlagRow = {
  id: number;
  key: DatabaseFeatureFlagKey;
  description: string;
  enabled: boolean;
  updatedAtFormatted?: string;
};

const DatabaseFeatureFlagsGrid = ({
  loading,
  error,
  responseFlags,
  draftFlags,
  onChange,
}: {
  loading: boolean;
  error: string | undefined;
  responseFlags: DatabaseFeatureFlagsResponseDto | null;
  draftFlags: UpdateDatabaseFeatureFlagsDto;
  onChange: (next: UpdateDatabaseFeatureFlagsDto) => void;
}) => {
  const language = useLanguage();

  const rows: FeatureFlagRow[] = useMemo(() => {
    if (error) return [];

    return DatabaseFeatureFlagKeys.map((key, id) => {
      const response = responseFlags?.[key];

      const enabled =
        key in draftFlags
          ? (draftFlags[key] ?? false)
          : (response?.enabled ?? false);

      return {
        id,
        key,
        description: language.databaseFeatureFlagDescription({ key }),
        enabled,
        updatedAtFormatted: response?.updatedAt
          ? formatDateStandard({ date: response.updatedAt, style: 'iso' })
          : undefined,
      };
    });
  }, [error, responseFlags, draftFlags, language]);

  const columns: GridColDef<FeatureFlagRow>[] = [
    { field: 'key', headerName: 'Feature', flex: 1 },
    { field: 'description', headerName: 'Description', flex: 2 },
    { field: 'updatedAtFormatted', headerName: 'Last Updated', flex: 1 },
    {
      field: 'enabled',
      headerName: 'Enabled',
      width: 140,
      renderCell: (params) => {
        const key = params.row.key;

        return (
          <Checkbox
            checked={params.row.enabled}
            onChange={(e) =>
              onChange({
                ...draftFlags,
                [key]: e.target.checked,
              })
            }
          />
        );
      },
    },
  ];

  return (
    <DataGridPro
      rows={rows}
      columns={columns}
      loading={loading}
      autoHeight
      hideFooter
      disableRowSelectionOnClick
      slots={
        error
          ? { noRowsOverlay: () => <ErrorOverlay message={error} /> }
          : undefined
      }
    />
  );
};

// eslint-disable-next-line complexity
export const DatabaseFeatureFlagsPage = () => {
  const language = useLanguage();
  const { organizationId, databaseId } = useParams();
  const navigate = useNavigateSafe();

  const [responseFlags, setResponseFlags] =
    useState<DatabaseFeatureFlagsResponseDto | null>(null);

  const [draftFlags, setDraftFlags] = useState<UpdateDatabaseFeatureFlagsDto>(
    {},
  );

  const getUrlFn = useCallback(() => {
    if (!organizationId || !databaseId) {
      return null;
    }
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/feature-flags`;
  }, [databaseId, organizationId]);

  const readDatabaseFeatureFlagsPattern = useSimpleReadPattern({
    getUrlFn,
    canRead: !!organizationId && !!databaseId,
    responseSchema: DatabaseFeatureFlagsResponseDto,
  });

  const isReadLoading = readDatabaseFeatureFlagsPattern.status === 'loading';

  useEffect(() => {
    if (
      readDatabaseFeatureFlagsPattern.status === 'done' &&
      readDatabaseFeatureFlagsPattern.entity
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResponseFlags(readDatabaseFeatureFlagsPattern.entity);
      setDraftFlags({});
    }
  }, [readDatabaseFeatureFlagsPattern]);

  const readError =
    readDatabaseFeatureFlagsPattern.status === 'error'
      ? 'errorReason' in readDatabaseFeatureFlagsPattern.error &&
        typeof readDatabaseFeatureFlagsPattern.error.errorReason === 'string'
        ? readDatabaseFeatureFlagsPattern.error.errorReason
        : language.unknownError()
      : null;

  const updateDatabaseFeatureFlagsPattern = useSimpleWritePattern({
    getUrlFn,
    method: 'PUT',
    canWrite: !!organizationId && !!databaseId && !isReadLoading,
    writeSchema: UpdateDatabaseFeatureFlagsDto,
    responseSchema: DatabaseFeatureFlagsResponseDto,
  });

  const updateError =
    updateDatabaseFeatureFlagsPattern.status === 'error'
      ? 'errorReason' in updateDatabaseFeatureFlagsPattern.error &&
        typeof updateDatabaseFeatureFlagsPattern.error.errorReason === 'string'
        ? updateDatabaseFeatureFlagsPattern.error.errorReason
        : language.unknownError()
      : null;

  const isLoading =
    isReadLoading || updateDatabaseFeatureFlagsPattern.status === 'saving';

  const canSave =
    updateDatabaseFeatureFlagsPattern.canWrite &&
    !isLoading &&
    Object.keys(draftFlags).length > 0 &&
    !updateError;

  const handleSave = useCallback(() => {
    if (
      !updateDatabaseFeatureFlagsPattern.canWrite ||
      isLoading ||
      Object.keys(draftFlags).length === 0
    ) {
      return;
    }

    updateDatabaseFeatureFlagsPattern.saveEntity(draftFlags, (response) => {
      setResponseFlags(response);
      setDraftFlags({});
    });
  }, [updateDatabaseFeatureFlagsPattern, isLoading, draftFlags]);

  const handleCancel = useCallback(() => {
    if (!organizationId || !databaseId || isLoading) {
      return;
    }
    navigate(`/organizations/${organizationId}/databases/edit/${databaseId}`);
  }, [isLoading, navigate, organizationId, databaseId]);

  return (
    <AdminLayout currentPage={'DatabaseFeatureFlagsPage'}>
      <Spacing direction="vertical" size="md">
        <AdminBreadcrumbs
          items={[
            { label: 'Home', to: '/admin' },
            { label: 'Organizations', to: '/organizations' },
            {
              label: 'Databases',
              to: `/organizations/${organizationId}/databases`,
            },
            {
              label: 'Edit Database',
              to: `/organizations/${organizationId}/databases/edit/${databaseId}`,
            },
            {
              label: 'Database Feature Flags',
              to: `/organizations/${organizationId}/databases/${databaseId}/feature-flags`,
            },
          ]}
        />

        <DatabaseFeatureFlagsGrid
          loading={isLoading}
          error={readError || updateError || undefined}
          responseFlags={responseFlags}
          draftFlags={draftFlags}
          onChange={setDraftFlags}
        />

        <Spacing
          size="md"
          direction="horizontal"
          className="w-full flex-row-reverse"
        >
          <Button
            variant="destructive"
            size="small"
            disabled={isLoading}
            behavior={{
              type: 'onClick',
              onClick: handleCancel,
            }}
          >
            {language.cancel()}
          </Button>
          <Button
            variant="primary"
            size="small"
            disabled={!canSave}
            behavior={{
              type: 'onClick',
              onClick: handleSave,
            }}
          >
            {language.save()}
          </Button>
        </Spacing>
      </Spacing>
    </AdminLayout>
  );
};
